<script setup lang="ts">
/**
 * AI001 §4/§5/§6 — the first-use gate.
 *
 * Three compliance conditions turn on the same fact: that the user was told
 * what this tool is, what may be entered into it, and that its output needs
 * checking — and that we can *show* they were told. An intranet PDF
 * evidences none of that. This does, per user, with a version and a
 * timestamp in the audit ledger.
 *
 * Deliberately blocking rather than dismissible. A notice someone can wave
 * away is not an acknowledgement, and "the user confirmed" is precisely the
 * claim being made. It appears once per policy version; re-issuing the
 * policy re-arms it for everyone.
 */
import { ref } from "vue";

import { brandRef } from "../brand-state";

const props = defineProps<{
  /** Policy version being acknowledged. Shown so the record is legible. */
  version: string;
  /** Link to the full policy. Empty renders no link. */
  url: string;
}>();

const emit = defineEmits<{ (e: "acknowledged"): void }>();

const busy = ref(false);

async function accept(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  emit("acknowledged");
}
</script>

<template>
  <div class="gate" role="dialog" aria-modal="true" aria-labelledby="gate-h">
    <div class="gate__card">
      <h2 id="gate-h" class="gate__title">
        Før du bruger {{ brandRef.productName }}
      </h2>

      <ul class="gate__points">
        <li>
          <strong>Du ser kun det, du allerede har adgang til.</strong>
          Adgangsrettigheder fra Entra gælder uændret i
          {{ brandRef.productName }}.
        </li>
        <li>
          <strong>Svar er AI-genererede og kan indeholde fejl.</strong>
          Klik på kildehenvisningerne, og kontrollér altid svaret, før du
          bruger det — især tal, datoer og juridiske forhold.
        </li>
        <li>
          <strong>Skriv ikke følsomme personoplysninger</strong>
          (helbred, race, religion, fagforening, seksuelle forhold) eller
          adgangskoder. CPR-numre kun til godkendte formål.
        </li>
        <li>
          <strong>Samtaler slettes automatisk efter 4 måneders inaktivitet.</strong>
          Værktøjet er ikke et arkiv.
        </li>
      </ul>

      <p v-if="props.url" class="gate__link">
        <a :href="props.url" target="_blank" rel="noopener noreferrer">
          Læs den fulde AI-politik og brugervejledning →
        </a>
      </p>

      <button class="gate__accept" :disabled="busy" @click="accept">
        {{ busy ? "Registrerer…" : "Jeg har læst og forstået" }}
      </button>

      <p class="gate__meta">
        Din bekræftelse registreres med dato og version
        ({{ props.version }}), så vi kan dokumentere den over for Compliance.
      </p>
    </div>
  </div>
</template>

<style scoped>
.gate {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: color-mix(in srgb, var(--color-bg) 82%, transparent);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.gate__card {
  width: min(38rem, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 24px 48px rgb(0 0 0 / 0.18));
  padding: var(--space-5, 2rem);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.gate__title {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.2;
  text-wrap: balance;
}
.gate__points {
  margin: 0;
  padding-left: 1.15rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--color-ink);
}
.gate__points strong {
  font-weight: 600;
}
.gate__link {
  margin: 0;
  font-size: 0.9rem;
}
.gate__accept {
  align-self: flex-start;
  padding: 0.6rem 1.4rem;
  font: inherit;
  font-weight: 600;
  color: #fff;
  background: var(--color-accent);
  border: none;
  border-radius: var(--radius-pill, 999px);
  cursor: pointer;
}
.gate__accept:hover:not(:disabled) {
  background: var(--color-accent-hover, var(--color-accent));
}
.gate__accept:disabled {
  opacity: 0.6;
  cursor: progress;
}
.gate__meta {
  margin: 0;
  font-size: 0.75rem;
  color: var(--color-muted);
}
</style>
